import React, { useEffect, useMemo, useRef, useState } from 'react';
import useMediaQuery from "@mui/system/useMediaQuery";
import { CircularProgress, Typography, useTheme } from "@mui/joy";
import { Divider } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { Box } from "@mui/system";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import _ from "lodash";
import { BasicModalContent, getInstalledBaseTypeIcon } from "@dreebit-registry/vsm-microservice-components";
import { ArrowRightAltRounded } from "@mui-symbols-material/w300";
import { MoveInstalledBaseNodeContext } from "../../graphql/queries/moveInstalledBaseNodeContext";
import { MoveInstalledBaseNodePreview } from "../../graphql/queries/moveInstalledBaseNodePreview";
import { MoveInstalledBaseNode } from "../../graphql/mutations/moveInstalledBaseNode";
import { InstalledBaseNodeTypeKey, MoveBlockingReason, TypeDefinitionAttribute } from "../../codegen/graphql";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { separateInstalledBaseEventAttributes } from "../../utils/separateInstalledBaseEventAttributes";
import {
    PUMP_AGE_INDEX,
    RUNNING_TIME_INDEX,
    RemoveSyncContext,
    computePriorRemovesTotal,
    evaluateRemoveValuesChange,
    finalizeRemoveSubmit,
    findAttributeValue,
    hasAttributeIndex,
    isManualPumpAgeEdit,
    toFiniteNumber,
} from "../../utils/moveWizardSync";
import { FormikHelpersRef } from "../../types";
import { IotSystemMatch, resolveIotSystemForNodes } from "../../utils/findIotSystemInNodePath";
import { getInstalledBaseNodeTypeName } from "../../utils/getInstalledBaseNodeTypeName";
import { getPathNodesAbove } from "../../utils/installedBasePath";
import AssetNameplate from "../AssetNameplate";
import InstalledBasePathLine from "../InstalledBasePathLine";
import InfrastructureBrowser from "../InfrastructureBrowser";
import TDAStepperForm from "../TDAStepperForm";
import WizardNavigation from "../Wizard/WizardNavigation";
import EventIotUpdatePrompt from "../CreateInstalledBaseEventModal/EventIotUpdatePrompt";

const DEFAULT_REFETCH = ["InstalledBaseNodeList", "InstalledBaseRootNodeList", "InstalledBaseNode"];

export interface MoveInstalledBaseNodeSuccessInfo {
    movedNodeId: string;
    targetParentNodeId: string;
    /** Root-to-leaf ancestor ids of the new parent, including the parent itself —
     *  lets the tree re-open its columns at the node's new location. */
    targetParentPathIds: string[];
}

interface Props {
    /** The installedBaseNode to move (NOT an assetLocation id). */
    nodeId: string;
    open: boolean;
    onClose: () => void;
    refetchQueries?: string[];
    /** Asset context for the IoT prompt's nameplate when moving an ASSET node from an asset view. */
    assetId?: string;
    assetLocationId?: string;
    /** Overrides the modal title and finish button — e.g. "Restore node" when the
     *  move is a restore out of the trash. Defaults to "Move node". */
    titleText?: string;
    onMoved?: (info: MoveInstalledBaseNodeSuccessInfo) => void;
}

type MovePhase = 'pick' | 'remove' | 'install';

/**
 * Wizard for moving an installed base node (with its whole subtree) under a new
 * parent within the same customer. Step 1 lets the user pick the new parent —
 * scoped to the node's own client — while `moveInstalledBaseNodePreview` runs on
 * every selection and either surfaces the typed blocking reasons or the lifecycle
 * forms (REMOVE at the source and/or INSTALL at the target) the move requires.
 * Each required form is its own step with an independent value object, mapping
 * 1:1 onto the mutation's removeAttributes/installAttributes lists (which must be
 * supplied exactly when the preview returned the matching form).
 */
const MoveInstalledBaseNodeModal = ({
    nodeId,
    open,
    onClose,
    refetchQueries,
    assetId,
    assetLocationId,
    titleText,
    onMoved,
}: Props) => {
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const effectiveTitle = titleText ?? t('Move node');
    const navigate = useNavigate();
    const apolloClient = useApolloClient();

    const [phase, setPhase] = useState<MovePhase>('pick');
    const [targetParentId, setTargetParentId] = useState<string | null>(null);
    const [removeValues, setRemoveValues] = useState<Record<string, any>>({});
    const [installValues, setInstallValues] = useState<Record<string, any>>({});
    const [submitting, setSubmitting] = useState(false);
    // When the moved asset's old or new location sits under an IoT system, the modal
    // stays open with this prompt; `onMoved` is deferred until the user dismisses it.
    const [iotPrompt, setIotPrompt] = useState<IotSystemMatch | null>(null);
    const finalizeRef = useRef<(() => void) | null>(null);

    // Prefill interdependency bookkeeping (see moveWizardSync.ts). Kept in refs, not
    // form values: separateInstalledBaseEventAttributes submits every non-`_` key.
    const prevRemoveValuesRef = useRef<Record<string, any> | null>(null);
    const removeFormikRef = useRef<FormikHelpersRef | null>(null);
    // Date-derived runningTime whose field wasn't on the mounted step yet.
    const pendingRunningTimeRef = useRef<number | null>(null);
    const pumpAgeLatchedRef = useRef(false);
    // Last auto-written pumpAge (or the BE default); undefined = uninitialized.
    const lastAutoPumpAgeRef = useRef<number | null | undefined>(undefined);
    const priorRemovesTotalRef = useRef(0);

    useEffect(() => {
        if (open) {
            setPhase('pick');
            setTargetParentId(null);
            setRemoveValues({});
            setInstallValues({});
            setIotPrompt(null);
            finalizeRef.current = null;
            prevRemoveValuesRef.current = null;
            removeFormikRef.current = null;
            pendingRunningTimeRef.current = null;
            pumpAgeLatchedRef.current = false;
            lastAutoPumpAgeRef.current = undefined;
            priorRemovesTotalRef.current = 0;
        }
    }, [open, nodeId]);

    const contextQuery = useQuery(MoveInstalledBaseNodeContext, {
        variables: { id: nodeId },
        skip: !open,
    });
    const movedNode = _.get(contextQuery, 'data.installedBaseNode');
    const movedNodeTitle: string = _.get(movedNode, 'title', '');
    const movedNodeTypeKey: InstalledBaseNodeTypeKey | undefined = _.get(movedNode, 'nodeType.key');
    // pathNodes is root-to-leaf and includes the node itself as last element.
    const pathNodes: Array<{ id: string; title?: string }> = _.get(movedNode, 'pathNodes', []) ?? [];
    const clientRootId: string | undefined = pathNodes[0]?.id;
    const currentParentId: string | undefined = pathNodes.length >= 2 ? pathNodes[pathNodes.length - 2].id : undefined;

    const previewQuery = useQuery(MoveInstalledBaseNodePreview, {
        variables: { id: nodeId, targetParentNodeId: targetParentId! },
        skip: !open || !targetParentId,
        fetchPolicy: 'network-only',
    });
    const preview = _.get(previewQuery, 'data.moveInstalledBaseNodePreview');
    const previewLoading = previewQuery.loading;
    const blockingReasons: MoveBlockingReason[] = _.get(preview, 'blockingReasons', []) ?? [];
    const removeAttrs: TypeDefinitionAttribute[] | null = _.get(preview, 'removeForm.typeDefinitionAttributes', null);
    const installAttrs: TypeDefinitionAttribute[] | null = _.get(preview, 'installForm.typeDefinitionAttributes', null);
    const hasRemoveForm = _.get(preview, 'removeForm') != null;
    const hasInstallForm = _.get(preview, 'installForm') != null;

    // Prefill interdependency: the removal's runningTime and the install's pump age
    // are linked (pumpAge = prior removals total + this removal's runningTime), and a
    // removalDate change re-derives runningTime from the source location's data.
    const runningTimeEnabled = hasRemoveForm && hasAttributeIndex(removeAttrs, RUNNING_TIME_INDEX);
    const cascadeEnabled = hasRemoveForm && hasInstallForm && hasAttributeIndex(installAttrs, PUMP_AGE_INDEX);
    const sourceInstallDate: string | null = _.get(movedNode, 'assetLocation.installationDate') ?? null;
    const averageRuntimePerDay = toFiniteNumber(_.get(movedNode, 'assetLocation.averageRuntimePerDay'));

    // Assembled at event time so the refs are read fresh.
    const getSyncCtx = (): RemoveSyncContext => ({
        priorRemovesTotal: priorRemovesTotalRef.current,
        sourceInstallDate,
        averageRuntimePerDay,
        pumpAgeLatched: pumpAgeLatchedRef.current,
        cascadeEnabled,
        runningTimeEnabled,
    });

    useEffect(() => {
        if (!preview) return;
        // The defaults are source-derived, so recomputing per preview (target change)
        // is idempotent.
        priorRemovesTotalRef.current = computePriorRemovesTotal(removeAttrs, installAttrs);
        // Initialize only once per session: a later preview returns the pristine BE
        // default again while the stash may already hold a cascaded value — re-seeding
        // would make the install form's mount echo look like a manual edit.
        if (lastAutoPumpAgeRef.current === undefined) {
            lastAutoPumpAgeRef.current = toFiniteNumber(findAttributeValue(installAttrs, PUMP_AGE_INDEX));
        }
    }, [preview, removeAttrs, installAttrs]);

    const applyPumpAgeWrite = (pumpAge: number) => {
        // The install form is never mounted while the remove side changes — stashing
        // suffices, the value seeds the form on mount.
        setInstallValues((prev) => ({ ...prev, [PUMP_AGE_INDEX]: pumpAge }));
        lastAutoPumpAgeRef.current = pumpAge;
    };

    const handleRemoveStepValues = (values: Record<string, any>) => {
        setRemoveValues((prev) => ({ ...prev, ...values }));

        // A date-derived runningTime computed while its field's step wasn't mounted
        // is flushed as soon as a step containing the field comes up.
        if (
            pendingRunningTimeRef.current != null &&
            RUNNING_TIME_INDEX in values &&
            removeFormikRef.current
        ) {
            const runningTime = pendingRunningTimeRef.current;
            pendingRunningTimeRef.current = null;
            removeFormikRef.current.setFieldValue(RUNNING_TIME_INDEX, runningTime);
            setRemoveValues((prev) => ({ ...prev, [RUNNING_TIME_INDEX]: runningTime }));
            prevRemoveValuesRef.current = {
                ...prevRemoveValuesRef.current,
                ...values,
                [RUNNING_TIME_INDEX]: runningTime,
            };
            return;
        }

        // The first fire is the mount echo carrying the BE defaults — baseline it.
        if (prevRemoveValuesRef.current === null) {
            prevRemoveValuesRef.current = { ...values };
            return;
        }

        const decision = evaluateRemoveValuesChange(prevRemoveValuesRef.current, values, getSyncCtx());
        let writtenRunningTime: number | undefined;

        if (decision.nextRunningTime !== undefined) {
            if (RUNNING_TIME_INDEX in values && removeFormikRef.current) {
                removeFormikRef.current.setFieldValue(RUNNING_TIME_INDEX, decision.nextRunningTime);
                setRemoveValues((prev) => ({ ...prev, [RUNNING_TIME_INDEX]: decision.nextRunningTime }));
                writtenRunningTime = decision.nextRunningTime;
            } else {
                // runningTime lives on a step that isn't mounted — park the value; the
                // submit-time finalize applies it even if that step is never shown.
                pendingRunningTimeRef.current = decision.nextRunningTime;
            }
        } else if (
            RUNNING_TIME_INDEX in values &&
            toFiniteNumber(values[RUNNING_TIME_INDEX]) !==
                toFiniteNumber(prevRemoveValuesRef.current[RUNNING_TIME_INDEX])
        ) {
            // A manual runningTime edit sticks until the next date change.
            pendingRunningTimeRef.current = null;
        }

        if (decision.nextPumpAge !== undefined) {
            applyPumpAgeWrite(decision.nextPumpAge);
        }

        // Include our own write so its Formik echo diffs empty (no double cascade,
        // not misread as a manual edit).
        prevRemoveValuesRef.current = {
            ...prevRemoveValuesRef.current,
            ...values,
            ...(writtenRunningTime !== undefined ? { [RUNNING_TIME_INDEX]: writtenRunningTime } : {}),
        };
    };

    const handleInstallStepValues = (values: Record<string, any>) => {
        setInstallValues((prev) => ({ ...prev, ...values }));
        if (!cascadeEnabled || pumpAgeLatchedRef.current) return;
        if (!(PUMP_AGE_INDEX in values)) return;
        if (isManualPumpAgeEdit(values[PUMP_AGE_INDEX], lastAutoPumpAgeRef.current)) {
            // The user overrode the pump age — their value wins for the session.
            pumpAgeLatchedRef.current = true;
        }
    };

    const isNoop = !!targetParentId && targetParentId === currentParentId;
    const canProceed =
        !!targetParentId &&
        !isNoop &&
        !previewLoading &&
        !previewQuery.error &&
        _.get(preview, 'allowed') === true;
    const formStepCount = (hasRemoveForm ? 1 : 0) + (hasInstallForm ? 1 : 0);

    // Breadcrumb of the picked parent, shown persistently across all steps.
    const targetPathQuery = useQuery(MoveInstalledBaseNodeContext, {
        variables: { id: targetParentId! },
        skip: !open || !targetParentId,
    });
    const targetPathNodes: Array<{ id: string; title?: string }> =
        _.get(targetPathQuery, 'data.installedBaseNode.pathNodes', []) ?? [];
    // The picked parent itself is the last crumb. pathNodes may or may not contain
    // it (root nodes come back with an empty path) — normalize via getPathNodesAbove.
    const targetNode = _.get(targetPathQuery, 'data.installedBaseNode');
    const targetPathCrumbs = targetNode
        ? [...getPathNodesAbove(targetNode), { id: targetNode.id, title: targetNode.title }]
        : [];

    const blockingReasonLabels: Record<MoveBlockingReason, string> = useMemo(() => ({
        [MoveBlockingReason.CROSS_CLIENT]: t('The selected node belongs to a different customer.'),
        [MoveBlockingReason.CYCLE]: t('A node cannot be moved into itself or its own subtree.'),
        [MoveBlockingReason.PROTECTED_NODE]: t('Protected elements such as structural roots and the trash cannot be moved.'),
        [MoveBlockingReason.MISSING_WRITE_ACCESS]: t('You need write access to the node, its current parent and the new parent.'),
        [MoveBlockingReason.TARGET_IS_ASSET]: t('An asset cannot be selected as the new parent.'),
        [MoveBlockingReason.TARGET_IN_TRASH]: t('Elements cannot be moved into the trash.'),
        // Structure rules the backend added with the IBM-1039 schema generation (system / limit checks).
        [MoveBlockingReason.INVALID_PARENT_TYPE]: t('This type of element cannot be placed under the selected parent.'),
        [MoveBlockingReason.INVALID_SYSTEM_STRUCTURE]: t('The move would break the structure of the IoT system.'),
        [MoveBlockingReason.LAST_PRESSURE_LINE]: t('The last pressure line of a system cannot be moved.'),
        [MoveBlockingReason.PRESSURE_LINE_LIMIT_EXCEEDED]: t('The new parent would exceed its maximum number of pressure lines.'),
        [MoveBlockingReason.ASSET_LIMIT_EXCEEDED]: t('The new parent would exceed its maximum number of assets.'),
    }), [t]);

    const [moveInstalledBaseNodeMutation] = useMutation(MoveInstalledBaseNode);

    const doSubmit = async (finalValues: { remove?: Record<string, any>; install?: Record<string, any> }) => {
        if (!targetParentId) return;
        // Arity rule: supply a list exactly when the preview returned the matching form.
        const removeAttributes = hasRemoveForm
            ? separateInstalledBaseEventAttributes(finalValues.remove ?? removeValues, removeAttrs ?? []).attributes
            : undefined;
        const installAttributes = hasInstallForm
            ? separateInstalledBaseEventAttributes(finalValues.install ?? installValues, installAttrs ?? []).attributes
            : undefined;
        setSubmitting(true);
        try {
            await moveInstalledBaseNodeMutation({
                variables: {
                    id: nodeId,
                    targetParentNodeId: targetParentId,
                    removeAttributes,
                    installAttributes,
                },
                refetchQueries: refetchQueries || DEFAULT_REFETCH,
            });

            toast.success(t('Node moved successfully'));

            // The moved node's path (and both parents' child/asset counts) changed —
            // drop them so any later visit queries fresh data.
            [nodeId, currentParentId, targetParentId].forEach((id) => {
                if (!id) return;
                apolloClient.cache.evict({ id: apolloClient.cache.identify({ __typename: 'InstalledBaseNode', id }) });
            });
            apolloClient.cache.gc();

            const targetPathIds = targetPathNodes.map((n) => n?.id).filter(Boolean);
            const finalize = () => onMoved?.({
                movedNodeId: nodeId,
                targetParentNodeId: targetParentId,
                targetParentPathIds: targetPathIds.length ? targetPathIds : [targetParentId],
            });

            // Moving an asset relocates it exactly like a REMOVE/INSTALL event pair —
            // offer the IoT config update when the new or old location touches an IoT system.
            if (movedNodeTypeKey === InstalledBaseNodeTypeKey.ASSET) {
                const iotSystem = await resolveIotSystemForNodes(apolloClient, [targetParentId, currentParentId]);
                if (iotSystem) {
                    finalizeRef.current = finalize;
                    setIotPrompt(iotSystem);
                    return;
                }
            }

            finalize();
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        } finally {
            setSubmitting(false);
        }
    };

    const dismissIotPrompt = () => {
        finalizeRef.current?.();
        finalizeRef.current = null;
        setIotPrompt(null);
        onClose();
    };

    const handleIotUpdate = () => {
        const systemId = iotPrompt?.systemId;
        dismissIotPrompt();
        if (systemId) {
            navigate(`/systems/${systemId}/iotEdit`);
        }
    };

    const handleModalClose = () => {
        // The move already succeeded when the prompt is up — closing still flushes onMoved.
        if (iotPrompt) {
            dismissIotPrompt();
            return;
        }
        onClose();
    };

    // An ASSET node's card shows the compact asset nameplate (image + key data)
    // like the event forms; the asset dashboard passes the location id directly,
    // tree-initiated moves resolve it from the node context query.
    const movedAssetLocationId: string | undefined =
        movedNodeTypeKey === InstalledBaseNodeTypeKey.ASSET
            ? (assetLocationId ?? _.get(movedNode, 'assetLocation.id'))
            : undefined;

    // Context card styled like the event form's nameplate box: the moved node on
    // top, then the picked new parent as a key/value row. Persists on every step.
    const contextHeader = (
        <Box
            sx={{
                mt: 1,
                mb: 1.5,
                p: 2,
                border: '1px solid #ccc',
                borderRadius: '5px',
            }}
        >
            {movedAssetLocationId ? (
                <AssetNameplate assetLocationId={movedAssetLocationId} compact disableNavigation showLocation />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        {getInstalledBaseTypeIcon(movedNodeTypeKey, 40)}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography
                            level="titleMedium"
                            fontWeight="bold"
                            title={movedNodeTitle}
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                            {movedNodeTitle}
                        </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {getInstalledBaseNodeTypeName(movedNode?.nodeType, t)}
                        </Typography>
                    </Box>
                </Box>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                <Typography level="body-sm" color="neutral" sx={{ flexShrink: 0 }}>
                    {t('New parent')}:
                </Typography>
                {targetParentId ? (
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <InstalledBasePathLine pathNodes={targetPathCrumbs} />
                    </Box>
                ) : (
                    <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        {t('No node selected.')}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    const renderPickStep = () => {
        // Until the preview settles, pretend a form step follows so the primary button
        // reads "Next"; a formless allowed move collapses to a single-step "Move node".
        const pickTotalSteps = canProceed ? 1 + formStepCount : 2;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {contextHeader}
                <InfrastructureBrowser
                    label={t('Choose the new parent node')}
                    rootNodeId={clientRootId}
                    rootSelectable
                    includeTrash={false}
                    value={targetParentId ?? ""}
                    onChange={(id) => setTargetParentId(id)}
                />
                <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
                    {t('All child nodes and assets will be moved along.')}
                </Typography>

                {!!targetParentId && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {isNoop ? (
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                {t('This is already the current parent.')}
                            </Typography>
                        ) : previewLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size="sm" />
                                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                    {t('Checking whether this move is possible…')}
                                </Typography>
                            </Box>
                        ) : previewQuery.error ? (
                            <Typography level="body-sm" color="danger">
                                {resolveErrorMessage(previewQuery.error, t)}
                            </Typography>
                        ) : blockingReasons.length > 0 ? (
                            blockingReasons.map((reason) => (
                                <Typography key={reason} level="body-sm" color="danger">
                                    {blockingReasonLabels[reason] ?? reason}
                                </Typography>
                            ))
                        ) : null}
                    </Box>
                )}

                <Box sx={{ pt: 2 }}>
                    <WizardNavigation
                        activeStep={0}
                        totalSteps={pickTotalSteps}
                        showProgress={false}
                        onBack={() => undefined}
                        onNext={() => setPhase(hasRemoveForm ? 'remove' : 'install')}
                        onFinish={() => doSubmit({})}
                        isNextDisabled={!canProceed}
                        isFinishDisabled={!canProceed}
                        isNextLoading={!!targetParentId && !isNoop && previewLoading}
                        isFinishLoading={submitting}
                        finishButtonText={effectiveTitle as string}
                    />
                </Box>
            </Box>
        );
    };

    const renderFormPhase = (side: 'remove' | 'install') => {
        const attrs = (side === 'remove' ? removeAttrs : installAttrs) ?? [];
        const isLast = side === 'install' || !hasInstallForm;

        const header = (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {contextHeader}
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                    <Typography level="title-md">
                        {side === 'remove' ? t('Removal details') : t('Installation details')}
                    </Typography>
                    <Typography
                        level="body-sm"
                        onClick={() => setPhase('pick')}
                        sx={{ color: 'primary.500', cursor: 'pointer', flexShrink: 0, '&:hover': { textDecoration: 'underline' } }}
                    >
                        {t('Change parent')}
                    </Typography>
                </Box>
            </Box>
        );

        return (
            <TDAStepperForm
                key={`${side}-${targetParentId}`}
                attributes={attrs}
                initialFormValues={side === 'remove' ? removeValues : installValues}
                headerContent={header}
                isSubmitting={isLast ? submitting : false}
                finishButtonText={(isLast ? effectiveTitle : t('Next')) as string}
                onFormikHelpersRef={side === 'remove' ? (ref) => { removeFormikRef.current = ref; } : undefined}
                onStepValuesChange={(_key, values) =>
                    side === 'remove' ? handleRemoveStepValues(values) : handleInstallStepValues(values)}
                onSubmit={async (merged) => {
                    if (side === 'remove') {
                        // Authoritative sync point: applies any pending date-derived
                        // runningTime and re-derives the pump age from the final value,
                        // independent of what could be live-written along the way.
                        const { mergedValues, pumpAgeWrite } = finalizeRemoveSubmit(
                            merged,
                            pendingRunningTimeRef.current,
                            getSyncCtx(),
                        );
                        pendingRunningTimeRef.current = null;
                        setRemoveValues((prev) => ({ ...prev, ...mergedValues }));
                        prevRemoveValuesRef.current = { ...prevRemoveValuesRef.current, ...mergedValues };
                        if (pumpAgeWrite !== undefined) {
                            applyPumpAgeWrite(pumpAgeWrite);
                        }
                        if (hasInstallForm) {
                            setPhase('install');
                            return;
                        }
                        await doSubmit({ remove: mergedValues });
                        return;
                    }
                    setInstallValues((prev) => ({ ...prev, ...merged }));
                    await doSubmit({ install: merged });
                }}
            />
        );
    };

    const renderBody = () => {
        if (iotPrompt) {
            return (
                <EventIotUpdatePrompt
                    assetId={assetId}
                    assetLocationId={assetLocationId}
                    systemId={iotPrompt.systemId}
                    systemTitle={iotPrompt.systemTitle}
                    onUpdate={handleIotUpdate}
                    onClose={dismissIotPrompt}
                />
            );
        }

        if (contextQuery.loading && !movedNode) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (phase === 'pick') return renderPickStep();
        return renderFormPhase(phase);
    };

    return (
        <Dialog open={open} onClose={handleModalClose} fullScreen={isFullScreen}>
            <BasicModalContent
                startIcon={<ArrowRightAltRounded />}
                onClose={handleModalClose}
                title={<Typography level={'titleSmallLight'}>{effectiveTitle}</Typography>}
            >
                <Box sx={{ position: 'relative' }}>
                    {renderBody()}
                </Box>
            </BasicModalContent>
        </Dialog>
    );
};

export default MoveInstalledBaseNodeModal;
